/** VISTOERA — Platform Admin Icon Asset Picker (Function ≠ Icon).
 *
 * Occupancy source of truth: Platform Node Catalog (ALL rows — ONLINE,
 * OFFLINE, NOT_READY). Never use NODE_DEFINITIONS defaults for ownership.
 *
 * VISTOERA_CANVAS_NODE_CATALOG_ICON_AVAILABILITY_TRUTH_AND_TENANT_GLOBAL_ICON_PROPAGATION_P0_V2
 */
import { PlusOutlined } from '@ant-design/icons'
import { Button, Input, Modal, Select, Space, Spin, Tag, Typography, message } from 'antd'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  platformListNodeCatalog,
  platformListNodeCatalogIcons,
  platformUploadNodeCatalogIcon,
  type PlatformCustomIconRow,
} from '../api'
import {
  ICON_CATEGORY_LABELS,
  listNodeIconCatalog,
  type IconAssetCategory,
} from '../workspace/contentProduction/canvas/nodeIconCatalog'
import {
  buildIconOccupancyFromCatalog,
  iconPickerStatusFor,
  type IconOccupancyOwner,
} from '../workspace/contentProduction/canvas/canvasNodeIconAssignment'
import { platformColors } from './theme'

interface Props {
  open: boolean
  /** Function being edited — shown in modal context. */
  functionLabel?: string | null
  functionNodeType?: string | null
  currentIconKey?: string | null
  confirmLoading?: boolean
  onCancel: () => void
  /** Called after select. Meta is optional for backward compatibility; V2 ignores reassign. */
  onSelect: (iconKey: string, meta?: { reassign: boolean; displaceNodeTypes: string[] }) => void
}

export default function NodeIconPicker({
  open,
  functionLabel,
  functionNodeType,
  currentIconKey,
  confirmLoading,
  onCancel,
  onSelect,
}: Props) {
  const catalog = listNodeIconCatalog()
  const [draftKey, setDraftKey] = useState<string | null>(currentIconKey ?? null)
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState<IconAssetCategory | 'all'>('all')
  const [customIcons, setCustomIcons] = useState<PlatformCustomIconRow[]>([])
  const [uploading, setUploading] = useState(false)
  const [occupancyLoading, setOccupancyLoading] = useState(false)
  const [occupancy, setOccupancy] = useState<Map<string, IconOccupancyOwner[]>>(() => new Map())
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!open) return
    setDraftKey(currentIconKey ?? null)
    setQuery('')
    setCategory('all')
    let cancelled = false
    setOccupancyLoading(true)
    void (async () => {
      try {
        const [iconsRes, catRes] = await Promise.all([
          platformListNodeCatalogIcons(),
          platformListNodeCatalog(),
        ])
        if (cancelled) return
        setCustomIcons(iconsRes.data.icons ?? [])
        // Full catalog including OFFLINE / NOT_READY — ownership must not
        // be filtered by availability or implementation_status.
        setOccupancy(buildIconOccupancyFromCatalog(catRes.data.entries ?? []))
      } catch {
        if (!cancelled) {
          setCustomIcons([])
          setOccupancy(new Map())
        }
      } finally {
        if (!cancelled) setOccupancyLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, currentIconKey])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return catalog.filter((entry) => {
      if (category !== 'all' && entry.category !== category) return false
      if (!q) return true
      const hay = `${entry.label} ${entry.key} ${entry.tags.join(' ')}`.toLowerCase()
      return hay.includes(q)
    })
  }, [catalog, category, query])

  const filteredCustom = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return customIcons
    return customIcons.filter((row) => {
      const hay = `${row.original_filename || ''} ${row.icon_ref}`.toLowerCase()
      return hay.includes(q)
    })
  }, [customIcons, query])

  const selectedBuiltin = catalog.find((e) => e.key === draftKey)
  const selectedCustom = customIcons.find((c) => c.icon_ref === draftKey)

  const statusFor = (key: string) => iconPickerStatusFor(key, occupancy, functionNodeType)

  const tryApply = () => {
    if (!draftKey) return
    const st = statusFor(draftKey)
    if (st.kind === 'other') {
      message.error(st.text)
      return
    }
    onSelect(draftKey)
  }

  const onUploadClick = () => fileInputRef.current?.click()

  const onFilePicked = async (file: File | undefined) => {
    if (!file) return
    setUploading(true)
    try {
      const res = await platformUploadNodeCatalogIcon(file)
      const row = res.data
      setCustomIcons((prev) => [row, ...prev.filter((c) => c.icon_ref !== row.icon_ref)])
      setDraftKey(row.icon_ref)
      message.success('图标已上传')
    } catch (err) {
      const e = err as { response?: { data?: { detail?: { message?: string } | string } }; message?: string }
      const detail = e?.response?.data?.detail
      const msg =
        typeof detail === 'string'
          ? detail
          : detail && typeof detail === 'object' && detail.message
            ? detail.message
            : e?.message || '上传失败'
      message.error(msg)
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const renderTile = (
    key: string,
    visual: React.ReactNode,
    label: string,
    testId: string,
  ) => {
    const selected = key === draftKey
    const status = statusFor(key)
    const usedByOther = status.kind === 'other'
    const disabled = confirmLoading || uploading || usedByOther || occupancyLoading
    return (
      <button
        key={key}
        type="button"
        data-testid={testId}
        data-icon-ref={key}
        data-icon-status={status.kind}
        disabled={disabled}
        onClick={() => {
          if (usedByOther) return
          setDraftKey(key)
        }}
        title={`${label} · ${status.text}`}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
          padding: '10px 6px',
          borderRadius: 8,
          border: selected ? `2px solid ${platformColors.primary}` : `1px solid ${platformColors.border}`,
          background: selected ? platformColors.primarySoft : 'transparent',
          cursor: disabled ? 'not-allowed' : 'pointer',
          color: selected ? platformColors.primary : undefined,
          boxShadow: selected ? `0 0 0 2px ${platformColors.primarySoft}` : undefined,
          opacity: usedByOther ? 0.55 : 1,
          filter: usedByOther ? 'grayscale(0.35)' : undefined,
        }}
      >
        <span style={{ fontSize: 22, display: 'inline-flex' }}>{visual}</span>
        <span
          style={{
            fontSize: 11,
            textAlign: 'center',
            lineHeight: 1.2,
            fontWeight: 600,
            color: platformColors.mutedText,
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
        <Tag
          color={status.kind === 'available' ? 'success' : status.kind === 'this' ? 'processing' : 'warning'}
          style={{ margin: 0, fontSize: 10, lineHeight: '16px', maxWidth: '100%', whiteSpace: 'normal' }}
        >
          {status.text}
        </Tag>
      </button>
    )
  }

  return (
    <Modal
      title="选择图标资源（Approved Icon Asset Catalog）"
      open={open}
      onCancel={onCancel}
      destroyOnClose
      width={720}
      footer={
        <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography.Text type="secondary">
              正在编辑：{functionLabel || '—'}
              {functionNodeType ? ` · ${functionNodeType}` : ''}
            </Typography.Text>
            <Typography.Text type="secondary">
              当前选择：
              {selectedBuiltin
                ? `${selectedBuiltin.label} (${selectedBuiltin.key})`
                : selectedCustom
                  ? `${selectedCustom.original_filename || selectedCustom.icon_ref}`
                  : draftKey || '未选择'}
              {functionLabel ? ` → 分配给：${functionLabel}` : ''}
            </Typography.Text>
          </div>
          <Space>
            <Button onClick={onCancel} disabled={confirmLoading}>
              取消
            </Button>
            <Button
              type="primary"
              disabled={
                !draftKey ||
                confirmLoading ||
                occupancyLoading ||
                (draftKey ? statusFor(draftKey).kind === 'other' : false)
              }
              loading={confirmLoading}
              onClick={tryApply}
              data-testid="node-icon-picker-apply"
            >
              保存
            </Button>
          </Space>
        </Space>
      }
    >
      <Spin spinning={!!confirmLoading || uploading || occupancyLoading}>
        <Space style={{ width: '100%', marginBottom: 12 }} wrap>
          <Input.Search
            allowClear
            placeholder="搜索名称 / tags（麦克风、waveform、audio…）"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ width: 320 }}
            data-testid="node-icon-picker-search"
          />
          <Select
            value={category}
            style={{ width: 140 }}
            onChange={(v) => setCategory(v)}
            options={(Object.keys(ICON_CATEGORY_LABELS) as Array<IconAssetCategory | 'all'>).map((k) => ({
              value: k,
              label: ICON_CATEGORY_LABELS[k],
            }))}
            data-testid="node-icon-picker-category"
          />
          <Button
            icon={<PlusOutlined />}
            onClick={onUploadClick}
            loading={uploading}
            data-testid="node-icon-picker-upload"
          >
            + 上传图标
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/webp"
            style={{ display: 'none' }}
            onChange={(e) => void onFilePicked(e.target.files?.[0])}
          />
        </Space>

        {filteredCustom.length > 0 && (
          <>
            <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
              自定义
            </Typography.Text>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
                gap: 8,
                marginBottom: 16,
              }}
            >
              {filteredCustom.map((row) =>
                renderTile(
                  row.icon_ref,
                  row.url ? (
                    <img src={row.url} alt="" style={{ width: 28, height: 28, objectFit: 'contain' }} />
                  ) : (
                    <span style={{ fontSize: 22, opacity: 0.5 }}>◇</span>
                  ),
                  row.original_filename || '自定义',
                  `node-icon-option-custom-${row.id}`,
                ),
              )}
            </div>
          </>
        )}

        <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
          内置
        </Typography.Text>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
            gap: 8,
            maxHeight: 360,
            overflowY: 'auto',
            paddingTop: 4,
          }}
        >
          {filtered.map((entry) =>
            renderTile(entry.key, entry.icon, entry.label, `node-icon-option-${entry.key}`),
          )}
        </div>
      </Spin>
    </Modal>
  )
}

